'use client';
import { Fade, Slide } from '@mui/material';
import { useEffect, useState } from 'react';
import { MOTION } from '../../constants';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { NoticeCard } from '../noticeCard';

// Short drops (e.g. switching networks) don't deserve a banner
const SHOW_DELAY_MS = 1000;
// Time the "back online" state stays up before sliding out
const RECOVERED_MS = 2000;

type BannerState = 'hidden' | 'offline' | 'recovered';

const CONTENT = {
  offline: {
    tone: 'warning',
    icon: 'wifi_off',
    title: 'Sin conexión a internet',
    body: 'Revisá tu Wi-Fi o datos móviles. Algunas acciones no van a funcionar.',
  },
  recovered: {
    tone: 'success',
    icon: 'wifi',
    title: 'Conexión restablecida',
    body: 'Ya podés seguir usando la app.',
  },
} as const;

/**
 * Floating card that slides in while the browser is offline and confirms when the connection is back.
 * Mount once per app, next to the theme provider.
 */
export const OfflineBanner = () => {
  const online = useOnlineStatus();
  const [state, setState] = useState<BannerState>('hidden');
  // Keeps the last content while the exit animation runs
  const [content, setContent] = useState<(typeof CONTENT)[keyof typeof CONTENT]>(CONTENT.offline);

  useEffect(() => {
    if (!online) {
      const timeout = setTimeout(() => setState('offline'), SHOW_DELAY_MS);
      return () => clearTimeout(timeout);
    }
    if (state !== 'offline') return;
    setState('recovered');
    const timeout = setTimeout(() => setState('hidden'), RECOVERED_MS);
    return () => clearTimeout(timeout);
  }, [online]);

  useEffect(() => {
    if (state !== 'hidden') setContent(CONTENT[state]);
  }, [state]);

  // Client-only: the server can't know the connection state, and rendering nothing there keeps hydration stable
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 8,
        left: 12,
        right: 12,
        zIndex: 1500,
        display: 'flex',
        justifyContent: 'center',
        // Informative only: clicks go through to the page underneath
        pointerEvents: 'none',
      }}
    >
      <Slide in={state !== 'hidden'} direction="down" timeout={MOTION.medium} mountOnEnter unmountOnExit>
        <Fade in={state !== 'hidden'} timeout={MOTION.short}>
          <div style={{ width: '100%', maxWidth: 480 }}>
            <NoticeCard tone={content.tone} icon={content.icon} title={content.title} body={content.body} />
          </div>
        </Fade>
      </Slide>
    </div>
  );
};
